/**
 * Per-agent UI language registry.
 *
 * The country coverage lives with the language option so a future country can
 * be added by extending this registry, without coupling locale selection to
 * group names or scattering country checks through the UI.
 */
export const DEFAULT_LANGUAGE = 'en' as const;

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', countries: ['Australia', 'Philippines'] },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', countries: ['Czech'] },
  { code: 'de', name: 'German', nativeName: 'Deutsch', countries: ['Germany'] },
  { code: 'es', name: 'Spanish', nativeName: 'Español', countries: ['Mexico'] },
  { code: 'fr', name: 'French', nativeName: 'Français', countries: ['France'] },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', countries: ['Croatia'] },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', countries: ['Hungary'] },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', countries: ['Indonesia'] },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', countries: ['Italy'] },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', countries: ['Japan'] },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', countries: ['Romania'] },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', countries: ['Slovakia'] },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', countries: ['Slovenia'] },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', countries: ['Vietnam'] },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const LANGUAGE_CODE_SET = new Set<string>(SUPPORTED_LANGUAGES.map(({ code }) => code));

/** Return the canonical language code, falling back to English safely. */
export function normalizeLanguage(value: unknown): SupportedLanguage {
  if (typeof value !== 'string') return DEFAULT_LANGUAGE;

  const normalized = value.trim().toLowerCase().replace(/_/g, '-');
  if (LANGUAGE_CODE_SET.has(normalized)) return normalized as SupportedLanguage;

  const baseLanguage = normalized.split('-')[0];
  return LANGUAGE_CODE_SET.has(baseLanguage) ? baseLanguage as SupportedLanguage : DEFAULT_LANGUAGE;
}

/** `{ value, label, description }` options for the agent language picker. */
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(({ code, name, nativeName, countries }) => ({
  value: code,
  label: code === 'en' ? name : `${name} · ${nativeName}`,
  description: countries.join(', '),
}));
